import type { RangePickerProps } from 'antd/es/date-picker';
import { validate } from 'email-validator';
import { useEffect } from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex, VFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import StyledDatePicker from '@/components/DatePicker';
import { AttrTextInput } from '@/components/Inputs';
import type { SelectOptionT } from '@/components/Select/types';
import { AttrText } from '@/components/Typography/NamedTypo';
import PropertyValue from '@/components/Typography/PropertyValue';
import Typo from '@/components/Typography/Typo';
import CompanyDropdown from '@/features/Account/components/CompanyDropdown';
import useAgentStore from '@/features/Agent/stores';
import AttrSelectDropdown from '@/features/Commons/Dropdown/AttrSelectDropdown';
import useColor from '@/hooks/domain/useColor';
import useCustomerAttributes from '@/hooks/domain/useCustomerAttributes';
import useUserRole from '@/hooks/domain/useUserRole';
import { useInputs } from '@/hooks/pure/useInputs';
import useCustomerStore from '@/stores/CustomerStore';
import usePopupStore from '@/stores/pure/PopupStore';
import useToastStore from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { DATE_FORMAT, getTimezoneDayjs, readableFormat } from '@/utils/time';

type InputStates = {
  email: string;
  phone: string;
  desc: string;
  reference_link: string;
  external_id: string;
  sns_id: string;
  usage: string;
  mrr: string;
};

const initialState: InputStates = {
  email: '',
  phone: '',
  desc: '',
  reference_link: '',
  external_id: '',
  sns_id: '',
  usage: '',
  mrr: '',
};

const CustomerInfoTab = (props: { customerId: string }) => {
  const { isEditor } = useUserRole();
  const color = useColor();
  const changeCustomerAttr = useCustomerStore(
    (state) => state.changeCustomerAttr,
  );

  const updateWorkspaceFieldOptions = useWorkspaceStore(
    (state) => state.updateWorkspaceFieldOptions,
  );

  const setPopup = usePopupStore((state) => state.setPopup);
  const addDefaultToast = useToastStore((state) => state.addDefaultToast);
  const customerMap = useCustomerStore((state) => state.customerMap);

  const customer = customerMap[props.customerId];
  const agentMap = useAgentStore((state) => state.agentMap);

  const {
    jobFunctionOptions,
    typeOptions,
    countryOptions,
    agentOptions,
    planOptions,
    stageOptions,
  } = useCustomerAttributes();

  const handleOptionDelete = (
    option: SelectOptionT,
    target: WorkspaceConfigsFields,
    itemName: string,
  ) => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: `Delete the ${itemName}`,
      message: `Are you sure you want to delete this ${itemName}?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => {
        updateWorkspaceFieldOptions(4, target, option.searchName);
      },
    });
  };

  const [inputs, onChange, onCustomChange] =
    useInputs<InputStates>(initialState);

  useEffect(() => {
    if (!customer) return;
    Object.keys(initialState).forEach((key) => {
      if (key === 'email') {
        onCustomChange('email', customer.email || '');
      } else if (key === 'usage') {
        onCustomChange(
          key,
          typeof customer.usage === 'number' ? customer.usage.toString() : '',
        );
      } else if (key === 'plan') {
        onCustomChange(
          key,
          typeof customer.mrr === 'number' ? customer.mrr.toString() : '',
        );
      } else {
        onCustomChange(key, customer[key as keyof InputStates] || '');
      }
    });
  }, [customer, onCustomChange]);

  const onBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (!customer) return;
    const name = e.currentTarget.name as keyof InputStates;
    if (!name) return;

    if (inputs[name] === '') {
      // reset attribute
      changeCustomerAttr(customer.alias, name, undefined);
      return;
    }

    switch (name) {
      case 'email': {
        if (inputs.email && !validate(inputs.email)) {
          addDefaultToast('Invalid email format.', 'fail');
          return;
        }
        if (customer.email !== inputs.email) {
          changeCustomerAttr(customer.alias, 'email', inputs.email);
        }
        return;
      }
      case 'usage': {
        if (customer.usage?.toString() !== inputs.usage) {
          changeCustomerAttr(customer.alias, 'usage', Number(inputs.usage));
          return;
        }
        return;
      }
      case 'mrr': {
        if (customer.mrr?.toString() !== inputs.mrr) {
          changeCustomerAttr(customer.alias, 'mrr', Number(inputs.mrr));
          return;
        }
        return;
      }
      default: {
        if (customer[name] !== inputs[name]) {
          changeCustomerAttr(customer.alias, name, inputs[name]);
        }
        return;
      }
    }
  };

  const onKeyEnter = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current > getTimezoneDayjs().startOf('day');
  };

  return (
    <Style>
      <VFlex g="30px">
        {customer ? (
          <VFlex g="5px">
            <Flex className="property-row">
              <AttrText className="property-label">Account</AttrText>
              <SelectValueWrapper>
                <CompanyDropdown
                  clearable
                  name={`customer-detail-${customer.alias}-company-dropdown`}
                  company_info={
                    (customer.company_info && {
                      name: customer.company_info?.name,
                      alias: customer.company_info.alias,
                    }) ||
                    null
                  }
                  changeCompany={(company) => {
                    changeCustomerAttr(
                      customer.alias,
                      'company_info',
                      company
                        ? {
                            alias: company.alias,
                            name: company.name,
                          }
                        : undefined,
                    );
                  }}
                  view="detail"
                />
              </SelectValueWrapper>
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Job Function</AttrText>
              <SelectValueWrapper>
                <AttrSelectDropdown
                  name={`customer-detail-${customer.alias}-position-dropdown`}
                  view="create"
                  selected={customer.job_function || null}
                  options={jobFunctionOptions}
                  onSelect={(key) =>
                    changeCustomerAttr(
                      customer.alias,
                      'job_function',
                      key,
                    )
                  }
                  onClear={() =>
                    changeCustomerAttr(
                      customer.alias,
                      'job_function',
                      undefined,
                    )
                  }
                  onCreate={async (key) => {
                    await updateWorkspaceFieldOptions(1, 'job_function', key);
                    changeCustomerAttr(
                      customer.alias,
                      'job_function',
                      key,
                    );
                  }}
                  onDelete={(key) =>
                    handleOptionDelete(key, 'job_function', 'Job function item')
                  }
                />
              </SelectValueWrapper>
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Email address</AttrText>
              <AttrTextInput
                name="email"
                value={inputs.email}
                placeholder="Email address"
                onChange={onChange}
                disabled={!isEditor}
                onKeyDown={onKeyEnter}
                onBlur={onBlur}
              />
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Phone number</AttrText>
              <AttrTextInput
                name="phone"
                placeholder="Enter phone number"
                value={inputs.phone}
                disabled={!isEditor}
                onChange={onChange}
                onKeyDown={onKeyEnter}
                onBlur={onBlur}
              />
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Description</AttrText>
              <AttrTextInput
                name="desc"
                placeholder="Enter Description"
                value={inputs.desc}
                disabled={!isEditor}
                onChange={onChange}
                onKeyDown={onKeyEnter}
                onBlur={onBlur}
              />
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Reference link</AttrText>
              <AttrTextInput
                name="reference_link"
                placeholder="Enter reference link"
                value={inputs.reference_link}
                disabled={!isEditor}
                onChange={onChange}
                onKeyDown={onKeyEnter}
                onBlur={onBlur}
              />
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Country</AttrText>
              <SelectValueWrapper>
                <AttrSelectDropdown
                  name={`customer-detail-${customer.alias}-country-dropdown`}
                  view="detail"
                  selected={customer.country || null}
                  options={countryOptions}
                  onSelect={(key) =>
                    changeCustomerAttr(customer.alias, 'country', key)
                  }
                  onClear={() =>
                    changeCustomerAttr(customer.alias, 'country', undefined)
                  }
                />
              </SelectValueWrapper>
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Type</AttrText>
              <SelectValueWrapper>
                <AttrSelectDropdown
                  name={`customer-detail-${customer.alias}-type-dropdown`}
                  view="detail"
                  selected={customer.type || null}
                  options={typeOptions}
                  onSelect={(key) =>
                    changeCustomerAttr(customer.alias, 'type', key)
                  }
                  onClear={() =>
                    changeCustomerAttr(customer.alias, 'type', undefined)
                  }
                  onCreate={async (key) => {
                    await updateWorkspaceFieldOptions(1, 'contact_type', key);
                    changeCustomerAttr(customer.alias, 'type', key);
                  }}
                  onDelete={(key) =>
                    handleOptionDelete(key, 'contact_type', 'Type item')
                  }
                />
              </SelectValueWrapper>
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Plan</AttrText>
              <SelectValueWrapper>
                <AttrSelectDropdown
                  name={`customer-detail-${customer.alias}-plan-dropdown`}
                  view="detail"
                  selected={customer.contact_plan || null}
                  options={planOptions}
                  onSelect={(key) =>
                    changeCustomerAttr(
                      customer.alias,
                      'contact_plan',
                      key,
                    )
                  }
                  onClear={() =>
                    changeCustomerAttr(
                      customer.alias,
                      'contact_plan',
                      undefined,
                    )
                  }
                  onCreate={async (key) => {
                    await updateWorkspaceFieldOptions(1, 'contact_plan', key);
                    changeCustomerAttr(
                      customer.alias,
                      'contact_plan',
                      key,
                    );
                  }}
                  onDelete={(key) =>
                    handleOptionDelete(key, 'contact_plan', 'Plan item')
                  }
                />
              </SelectValueWrapper>
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Stage</AttrText>
              <SelectValueWrapper>
                <AttrSelectDropdown
                  name={`customer-detail-${customer.alias}-stage-dropdown`}
                  view="detail"
                  selected={customer.contact_stage || null}
                  options={stageOptions}
                  onSelect={(key) =>
                    changeCustomerAttr(
                      customer.alias,
                      'contact_stage',
                      key,
                    )
                  }
                  onClear={() =>
                    changeCustomerAttr(
                      customer.alias,
                      'contact_stage',
                      undefined,
                    )
                  }
                  onCreate={async (key) => {
                    await updateWorkspaceFieldOptions(1, 'contact_stage', key);
                    changeCustomerAttr(
                      customer.alias,
                      'contact_stage',
                      key,
                    );
                  }}
                  onDelete={(key) =>
                    handleOptionDelete(key, 'contact_stage', 'Stage item')
                  }
                />
              </SelectValueWrapper>
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Monthly spend</AttrText>
              <AttrTextInput
                name="mrr"
                type="number"
                placeholder="Contact's monthly subscription amount"
                value={inputs.mrr || ''}
                disabled={!isEditor}
                onChange={(e) => {
                  if (Number(e.target.value) < 0) {
                    addDefaultToast(
                      'Monthly spend must be equal to or greater than 0',
                      'fail',
                    );
                    return;
                  }
                  onChange(e);
                }}
                onKeyDown={onKeyEnter}
                onBlur={onBlur}
              />
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">ID</AttrText>
              <AttrTextInput
                name="external_id"
                placeholder="Enter ID"
                value={inputs.external_id}
                disabled={!isEditor}
                onChange={onChange}
                onKeyDown={onKeyEnter}
                onBlur={onBlur}
              />
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">SNS ID</AttrText>
              <AttrTextInput
                name="sns_id"
                placeholder="Enter SNS ID"
                value={inputs.sns_id}
                disabled={!isEditor}
                onChange={onChange}
                onKeyDown={onKeyEnter}
                onBlur={onBlur}
              />
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Usage</AttrText>
              <AttrTextInput
                name="usage"
                type="number"
                placeholder="Enter usage volume"
                value={inputs.usage || ''}
                disabled={!isEditor}
                onChange={(e) => {
                  if (Number(e.target.value) < 0) {
                    addDefaultToast(
                      'Usage must be equal to or greater than 0',
                      'fail',
                    );
                    return;
                  }
                  onChange(e);
                }}
                onKeyDown={onKeyEnter}
                onBlur={onBlur}
              />
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Agent</AttrText>
              <SelectValueWrapper>
                <AttrSelectDropdown
                  name={`customer-detail-${customer.alias}-agent-dropdown`}
                  view="detail"
                  selected={
                    customer.agent
                      ? agentMap[customer.agent]?.name || null
                      : null
                  }
                  options={agentOptions}
                  onSelect={(key) =>
                    changeCustomerAttr(customer.alias, 'agent', key)
                  }
                  onClear={() =>
                    changeCustomerAttr(customer.alias, 'agent', undefined)
                  }
                />
              </SelectValueWrapper>
            </Flex>
            {customer?.nps && (
              <Flex className="property-row">
                <AttrText className="property-label">NPS Score</AttrText>
                <PropertyValue readOnly value={customer.nps} />
              </Flex>
            )}
            {customer?.ces && (
              <Flex className="property-row">
                <AttrText className="property-label">CES Score</AttrText>
                <PropertyValue readOnly value={customer.ces} />
              </Flex>
            )}
            {customer?.csat && (
              <Flex className="property-row">
                <AttrText className="property-label">CSAT Score</AttrText>
                <PropertyValue readOnly value={customer.csat} />
              </Flex>
            )}
            <Flex className="property-row">
              <AttrText className="property-label">Feedback</AttrText>
              <PropertyValue
                value={customer.feedback_count?.toString() || '0'}
              />
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Last activity</AttrText>
              {customer.latest_activity ? (
                <PropertyValue
                  value={readableFormat(customer.latest_activity)}
                />
              ) : (
                <Typo
                  type="label2"
                  weight="r"
                  color={color.gray500}
                  style={{ paddingLeft: 11, fontSize: 12 }}
                >
                  Last activity
                </Typo>
              )}
            </Flex>
            <Flex className="property-row">
              <AttrText className="property-label">Date</AttrText>
              <StyledDatePicker
                name="date"
                onChange={(_, dataString) => {
                  const parsedDate = getTimezoneDayjs(dataString).toJSON();

                  changeCustomerAttr(customer.alias, 'date', parsedDate);
                }}
                disabledDate={disabledDate}
                getPopupContainer={(triggerNode) => {
                  return triggerNode.parentNode as HTMLDivElement;
                }}
                value={
                  customer.date
                    ? getTimezoneDayjs(readableFormat(customer.date))
                    : null
                }
                format={DATE_FORMAT.readable}
              />
            </Flex>
          </VFlex>
        ) : (
          <CenterFlex style={{ height: 240 }}>
            <Spinner />
          </CenterFlex>
        )}
      </VFlex>
    </Style>
  );
};

export default CustomerInfoTab;

const Style = styled.div`
  .property-label {
    width: 120px;
    height: 24px;
    line-height: 24px;
  }

  .property-row {
    gap: 10px;
    align-items: center;
    height: 32px;
  }

  .email-input {
    font-size: 12px;
    height: 32px;

    &::placeholder {
      font-size: 12px;
    }
  }
`;

const SelectValueWrapper = styled(Flex)`
  width: calc(100% - 100px);
`;
